import type {
  ICellRendererParams,
  ProcessCellForExportParams,
} from "ag-grid-community";
import {
  formatDate as formatDateUtil,
  formatDateForExport,
  formatDateTime as formatDateTimeUtil,
  formatDateTimeForExport,
} from "./formatDate";

/** Muted currency next to loan amounts — matches grid text #182737 at ~50% opacity */
const CURRENCY_MUTED_COLOR = "rgba(24, 39, 55, 0.5)";

export const numberFormatter = (params: any, appendCurrency = false) => {
  const value = params.value;
  const currencyCode =
    params.data?.loan_amount_currency ?? params.currency ?? "";

  if (typeof value === "number") {
    if (appendCurrency && currencyCode) {
      return `${value.toLocaleString("en-GB")} ${currencyCode}`;
    }
    return value.toLocaleString("en-GB");
  }
  if (appendCurrency && value !== null && value !== undefined) {
    return currencyCode ? `${value} ${currencyCode}` : String(value);
  }
  return value;
};

/** AG Grid cell: formatted number + de-emphasised currency (pipeline loan columns). */
export function LoanAmountCurrencyCellRenderer(params: ICellRendererParams) {
  const value = params.value;
  const currency =
    (params.data as { loan_amount_currency?: string } | undefined)
      ?.loan_amount_currency ?? (params as { currency?: string }).currency;

  if (value === null || value === undefined || value === "" || value === 0) {
    return null;
  }

  const numberPart =
    typeof value === "number" ? value.toLocaleString("en-GB") : String(value);

  if (!currency) {
    return <>{numberPart}</>;
  }

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        height: "100%",
        gap: 6,
        minWidth: 0,
      }}
    >
      <span>{numberPart}</span>
      <span style={{ color: CURRENCY_MUTED_COLOR, flexShrink: 0 }}>
        {currency}
      </span>
    </span>
  );
}

export const currencyFormatter = (params: any) => {
  const value = params.value;
  if (value === null || value === undefined) return "-";
  if (typeof value === "number") {
    return new Intl.NumberFormat("en-GB", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }
  return value;
};

export const percentageFormatter = (params: any) => {
  const value = params.value;
  if (value === null || value === undefined) return "-";
  if (typeof value === "number") {
    return `${value}%`;
  }
  return value;
};

/** Normalise any backend date string to HTML date input format (YYYY-MM-DD) for AG Grid date editors. */
export function normaliseToDateInput(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().split("T")[0];
}

// Ag-grid formatter wrapper for formatDate utility
export const dateFormatter = (params: any) => {
  return formatDateUtil(params.value);
};

// Ag-grid formatter wrapper for formatDateTime utility
export const dateTimeFormatter = (params: any) => {
  return formatDateTimeUtil(params.value);
};

/**
 * `processCellCallback` for exportDataAsCsv: the grid shows dd-MMM-yy, but
 * CSV exports keep dd/MM/yyyy (and dd/MM/yyyy HH:mm) for columns using
 * dateFormatter / dateTimeFormatter. A column can also give its own export
 * text with `context.exportValue(row)`. Every other cell exports as the grid
 * would without a callback.
 */
export const csvExportCell = (params: ProcessCellForExportParams): string => {
  const colDef = params.column.getColDef();
  const exportValue = colDef.context?.exportValue;
  if (typeof exportValue === "function") {
    return String(exportValue(params.node?.data) ?? "");
  }
  if (colDef.valueFormatter === dateFormatter) {
    return formatDateForExport(params.value);
  }
  if (colDef.valueFormatter === dateTimeFormatter) {
    return formatDateTimeForExport(params.value);
  }
  return params.formatValue(params.value);
};

// Ag-grid formatter: dd-MMM-yy, blank for empty values
export const shortDateFormatter = (params: any) => {
  if (!params.value) return "";
  return formatDateUtil(params.value);
};
